import type { FilterGroup, FilterRule } from '../events/workspace.js';

// ─── Drilldown Group By Feature allowlists ───────────────────────────
// Curated product lists: Type='Drilldown' Feature values that data-sync publishes
// AND that the Drilldown grouping CASE can resolve. Not the CASE labels themselves
// — those include every `Similar *` variant, which the UI deliberately hides
// because they expose internal cluster IDs.
//
// `GET /drilldown/group-by-options` filters `features_mapping` rows against these
// before caching, so a mapping reload cannot surface an unsupported Group By
// that would silently fall through the CASE ELSE. The workspace guard test
// asserts each list is a subset of the matching SEGMENT_EXPRESSIONS labels.

export const DRILLDOWN_GROUP_BY_FEATURES_ALL = [
  'Hash',
  'Exact Text Overlay (Hook)',
  'Exact Voice Over (Hook)',
  'Exact Text Overlay (Post-Hook)',
  'Exact Voice Over (Post-Hook)',
  'Exact Visual (Hook)',
  'Exact Visual (Post-Hook)',
  'Full Text Overlay',
  'Full Voice Over',
  'Full Visual',
  'Ad ID',
  'Headline',
  'Primary',
  'Start Date',
  'Link',
  'Hook',
  'Body',
] as const;

export const DRILLDOWN_GROUP_BY_FEATURES_CORE = [
  'Client',
  'Account Name',
  'Ad Name',
  'Adset ID',
  'Adset Name',
  'Campaign Name',
  'Campaign ID',
] as const;

export const DRILLDOWN_GROUP_BY_FEATURES_COMP = ['Industry', 'Competitor'] as const;

export const DRILLDOWN_GROUP_BY_FEATURES_MY_BRAND = [
  ...DRILLDOWN_GROUP_BY_FEATURES_ALL,
  ...DRILLDOWN_GROUP_BY_FEATURES_CORE,
] as const;

export const DRILLDOWN_GROUP_BY_FEATURES_COMPETITORS = [
  ...DRILLDOWN_GROUP_BY_FEATURES_ALL,
  ...DRILLDOWN_GROUP_BY_FEATURES_COMP,
] as const;

// ─── Segment "none" + dimension advanced-filter fields ───────────────
// An empty Group By slot. On slot 1 it means "No grouping": the query falls back to
// the per-asset key, so the result has one row per asset.
export const DRILLDOWN_SEGMENT_NONE = 'All';

// Advanced-filter rules whose field is `dim:<Group By label>` filter the raw rows
// on that dimension BEFORE aggregation, so they work whether or not the dimension
// is grouped. Legacy `Groupby1/2/3` fields still filter the grouped rows.
export const DRILLDOWN_DIMENSION_FILTER_PREFIX = 'dim:';

export function toDrilldownDimensionField(label: string): string {
  return `${DRILLDOWN_DIMENSION_FILTER_PREFIX}${label}`;
}

/** Group By label of a `dim:` field, or null when the field is not a dimension field. */
export function parseDrilldownDimensionField(field: string): string | null {
  if (!field.startsWith(DRILLDOWN_DIMENSION_FILTER_PREFIX)) return null;
  const label = field.slice(DRILLDOWN_DIMENSION_FILTER_PREFIX.length);
  return label === '' ? null : label;
}

/**
 * True when an `Or` would join a dimension rule to a metric rule. Dimension rules filter raw
 * rows before aggregation and metric rules filter grouped rows after, so the query can only
 * AND the two levels together. That split is exact when every group connector is `And` and
 * every group mixing both kinds uses `And` between its rules (And binds tighter than Or, so
 * `dimA OR dimB AND metric` cannot be split either). The first group's connector and each
 * group's first rule connector are ignored, as in the SQL builder.
 */
export function hasCrossLevelOr(groups: readonly FilterGroup[]): boolean {
  const rules = groups.flatMap((group) => group.rules);
  const hasDimension = rules.some(isDimensionRule);
  const hasMetric = rules.some((rule) => !isDimensionRule(rule));
  if (!hasDimension || !hasMetric) return false;

  const hasGroupOr = groups.slice(1).some((group) => group.connector === 'Or');
  return hasGroupOr || groups.some(isMixedGroupWithOr);
}

function isDimensionRule(rule: FilterRule): boolean {
  return parseDrilldownDimensionField(rule.field) !== null;
}

function isMixedGroupWithOr(group: FilterGroup): boolean {
  const isMixed =
    group.rules.some(isDimensionRule) && group.rules.some((rule) => !isDimensionRule(rule));
  return isMixed && group.rules.slice(1).some((rule) => rule.connector === 'Or');
}
